import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ImageLightbox from './ImageLightbox';
import FolderManager from './FolderManager';
import BulkOperationsToolbar from './BulkOperationsToolbar';
import FilterHeader, { FilterState } from './FilterHeader';
import FolderCard from './FolderCard';
import Breadcrumb from './Breadcrumb';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface GenerationResult {
  id: number;
  job_id: number;
  result_type: string;
  file_path: string;
  file_size: number;
  metadata: any;
  thumbnail_path: string;
  created_at: string;
  status: string;
  folder_path: string | null;
  user_tags: string[];
  job_name: string;
  workflow_name: string;
  seed_used: number;
}

interface UserFolder {
  id: number;
  user_id: number;
  name: string;
  hotkey: string;
  color: string;
  created_at: string;
  image_count?: number;
  video_count?: number;
  preview_images?: Array<{
    id: number;
    thumbnail_path: string;
    file_path: string;
  }>;
}

interface ImageGalleryProps {
  jobId?: number;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ jobId }) => {
  const [images, setImages] = useState<GenerationResult[]>([]);
  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<number>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [currentFolderPath, setCurrentFolderPath] = useState<string | null>(null);

  // Helper to detect if file is a video (handles URLs with query parameters)
  const isVideo = (url: string): boolean => {
    return /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i.test(url);
  };

  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const BATCH_SIZE = 100;

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    folder: null,
    jobId: jobId || null,
    jobName: null,
    workflow: null,
    dateFrom: null,
    dateTo: null,
    search: '',
    status: 'active'
  });

  // Sync filters when jobId prop changes - fetch job name for filter display
  useEffect(() => {
    const fetchJobName = async () => {
      if (jobId) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_BASE}/jobs/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setFilters(prev => ({
            ...prev,
            jobId: jobId,
            jobName: response.data.job_name
          }));
        } catch (error) {
          console.error('Failed to fetch job name:', error);
          // Still set jobId even if we can't get the name
          setFilters(prev => ({ ...prev, jobId: jobId }));
        }
      } else {
        setFilters(prev => ({ ...prev, jobId: null, jobName: null }));
      }
    };

    fetchJobName();
  }, [jobId]);

  // Re-fetch when ANY filter changes (all filtering is backend now)
  useEffect(() => {
    fetchImages(true);
    fetchTotalCount();
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Sync currentFolderPath with filters.folder (when filter is cleared externally)
  useEffect(() => {
    if (filters.folder === null && currentFolderPath !== null) {
      setCurrentFolderPath(null);
    }
  }, [filters.folder, currentFolderPath]);

  const fetchImages = async (reset: boolean = false) => {
    try {
      const token = localStorage.getItem('token');

      if (reset) {
        setLoading(true);
        setImages([]);
        setHasMore(true);
      }

      // Fetch images in batches
      const offset = reset ? 0 : images.length;
      const params = new URLSearchParams();
      params.append('limit', BATCH_SIZE.toString());
      params.append('offset', offset.toString());
      params.append('status', filters.status);

      // Add ALL filters to backend request
      if (filters.folder !== null) {
        params.append('folder', filters.folder === 'ungrouped' ? '' : filters.folder);
      }
      if (filters.jobId) {
        params.append('job_id', filters.jobId.toString());
      }
      if (filters.jobName) {
        params.append('job_name', filters.jobName);
      }
      if (filters.workflow) {
        params.append('workflow', filters.workflow);
      }
      if (filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }

      const response = await axios.get(`${API_BASE}/gallery?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const newImages = response.data;

      if (reset) {
        setImages(newImages);
      } else {
        setImages(prev => [...prev, ...newImages]);
      }

      // Check if we have more images to load
      setHasMore(newImages.length === BATCH_SIZE);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch images:', err);
      setError('Failed to load images');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreImages = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    await fetchImages(false);
  };

  const fetchTotalCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('status', filters.status);

      // Add ALL filters to backend request
      if (filters.folder !== null) {
        params.append('folder', filters.folder === 'ungrouped' ? '' : filters.folder);
      }
      if (filters.jobId) {
        params.append('job_id', filters.jobId.toString());
      }
      if (filters.jobName) {
        params.append('job_name', filters.jobName);
      }
      if (filters.workflow) {
        params.append('workflow', filters.workflow);
      }
      if (filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }

      const response = await axios.get(`${API_BASE}/gallery/count?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch total count:', error);
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('status', filters.status);

      // Add ALL filters to backend request (same as images)
      if (filters.jobName) {
        params.append('job_name', filters.jobName);
      }
      if (filters.workflow) {
        params.append('workflow', filters.workflow);
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }

      const response = await axios.get(`${API_BASE}/folders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFolders(response.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const handleImageClick = (clickedImage: GenerationResult) => {
    const index = images.findIndex(img => img.id === clickedImage.id);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const handleLightboxImageChange = (newIndex: number) => {
    setLightboxIndex(newIndex);
  };

  const handleArchive = (imageId: number) => {
    // Remove from current view
    setImages(images.filter(img => img.id !== imageId));
    // Decrement total count since image left the current status view
    setTotalCount(prev => Math.max(0, prev - 1));
  };

  const handleImageUpdate = (imageId: number, updates: Partial<GenerationResult>) => {
    // Update images in current view
    setImages(images.map(img =>
      img.id === imageId ? { ...img, ...updates } : img
    ));
  };

  const handleFoldersChange = () => {
    // Refresh folders, images, and count when folders change
    fetchFolders();
    fetchImages(true);
    fetchTotalCount();
  };

  // Handle image selection with auto-clear when selection reaches 0
  const handleImageSelect = useCallback((imageId: number, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      setSelectedImageIds(prev => {
        const newSelection = new Set(prev);
        if (newSelection.has(imageId)) {
          newSelection.delete(imageId);
        } else {
          newSelection.add(imageId);
        }

        // Auto-clear selection mode when no images are selected
        setIsSelectionMode(newSelection.size > 0);

        return newSelection;
      });
    } else if (isSelectionMode) {
      // In selection mode, regular clicks also toggle selection
      event.preventDefault();
      setSelectedImageIds(prev => {
        const newSelection = new Set(prev);
        if (newSelection.has(imageId)) {
          newSelection.delete(imageId);
        } else {
          newSelection.add(imageId);
        }
        // Auto-clear selection mode when no images are selected
        setIsSelectionMode(newSelection.size > 0);
        return newSelection;
      });
    } else {
      // Normal click opens lightbox
      const image = images.find(img => img.id === imageId);
      if (image) {
        handleImageClick(image);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectionMode, images]);

  // Handle keyboard shortcuts with improved Escape behavior
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && event.key === 'a') {
      event.preventDefault();
      const allIds = new Set(images.map(img => img.id));
      setSelectedImageIds(allIds);
      setIsSelectionMode(true);
    }
    if (event.key === 'Escape') {
      // Escape always clears selection, regardless of count
      if (selectedImageIds.size > 0 || isSelectionMode) {
        event.preventDefault();
        setSelectedImageIds(new Set());
        setIsSelectionMode(false);
      }
    }
  }, [selectedImageIds, isSelectionMode, images]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (loading || isLoadingMore || !hasMore) return;

      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 500; // Load more when 500px from bottom

      if (scrollPosition >= threshold) {
        loadMoreImages();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isLoadingMore, hasMore, images.length]);

  // Selection operations
  const selectAllImages = () => {
    const allIds = new Set(images.map(img => img.id));
    setSelectedImageIds(allIds);
    setIsSelectionMode(true);
  };

  const clearSelection = () => {
    setSelectedImageIds(new Set());
    setIsSelectionMode(false);
  };

  // Bulk operations
  const handleBulkArchive = async () => {
    if (selectedImageIds.size === 0) return;

    const confirmMessage = `Are you sure you want to archive ${selectedImageIds.size} media?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('token');
      const archivedCount = selectedImageIds.size;

      // Archive all selected images
      await Promise.all(
        Array.from(selectedImageIds).map(imageId =>
          axios.post(`${API_BASE}/gallery/${imageId}/archive`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );

      // Remove archived images from view
      setImages(images.filter(img => !selectedImageIds.has(img.id)));
      // Decrement total count by number of archived images
      setTotalCount(prev => Math.max(0, prev - archivedCount));
      clearSelection();
    } catch (error) {
      console.error('Failed to bulk archive:', error);
      alert('Failed to archive some images. Please try again.');
    }
  };

  const handleBulkMoveToFolder = async (folderName: string) => {
    if (selectedImageIds.size === 0) return;

    try {
      const token = localStorage.getItem('token');
      const movedCount = selectedImageIds.size;

      // Move all selected images to folder
      await Promise.all(
        Array.from(selectedImageIds).map(imageId =>
          axios.put(`${API_BASE}/gallery/${imageId}/folder`, {
            folder_path: folderName || null
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );

      // Check if we're viewing a specific folder and images are moving OUT of it
      const isMovingOutOfCurrentFolder =
        filters.folder !== null &&
        filters.folder !== folderName &&
        !(filters.folder === 'ungrouped' && !folderName); // Handle ungrouped case

      if (isMovingOutOfCurrentFolder) {
        // Remove images from view and decrement count
        setImages(images.filter(img => !selectedImageIds.has(img.id)));
        setTotalCount(prev => Math.max(0, prev - movedCount));
      } else {
        // Just update folder_path, images stay in view
        setImages(images.map(img =>
          selectedImageIds.has(img.id)
            ? { ...img, folder_path: folderName || null }
            : img
        ));
      }

      clearSelection();
    } catch (error) {
      console.error('Failed to bulk move to folder:', error);
      alert('Failed to move some images. Please try again.');
    }
  };

  // Navigation functions for folder structure
  const navigateToFolder = (folderName: string) => {
    setCurrentFolderPath(folderName);
    setFilters(prev => ({ ...prev, folder: folderName }));
    setSelectedImageIds(new Set());
    setIsSelectionMode(false);
  };

  const navigateToRoot = () => {
    setCurrentFolderPath(null);
    setFilters(prev => ({ ...prev, folder: null }));
    setSelectedImageIds(new Set());
    setIsSelectionMode(false);
  };

  const handleBreadcrumbNavigate = (index: number) => {
    if (index === 0) {
      // Navigate to root (Gallery)
      navigateToRoot();
    }
    // For now, we only have 2 levels: Gallery and Folder
    // If we add subfolders later, this would handle intermediate levels
  };

  if (loading) {
    return (
      <div>
        <FilterHeader 
          filters={filters}
          onFiltersChange={setFilters}
          imageCounts={{ total: 0, filtered: 0 }}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading images...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <FilterHeader 
          filters={filters}
          onFiltersChange={setFilters}
          imageCounts={{ total: 0, filtered: 0 }}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Sticky Filter Header */}
      <FilterHeader
        filters={filters}
        onFiltersChange={setFilters}
        imageCounts={{
          total: totalCount,      // Backend-filtered total count
          filtered: images.length // Frontend-filtered display count
        }}
      />

      <div className="px-6 py-4">
        {/* Controls Row */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <span>Click to view full size</span>
            <span>Ctrl+click to select multiple</span>
            <span>Ctrl+A to select all</span>
            <span>Escape to clear selection</span>
            <span>← → Navigate in lightbox</span>
            <span>Mouse wheel to zoom</span>
            <span>Delete to archive</span>
            <span>1-9, A-Z to move to folders</span>
          </div>
          
          <button
            onClick={() => setShowFolderManager(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2 whitespace-nowrap"
          >
            <span>📁</span>
            <span>Manage Folders</span>
          </button>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : (
          <>
            {/* Breadcrumb Navigation (always show if in folder, even with no results) */}
            {currentFolderPath !== null && (
              <Breadcrumb
                path={['Gallery', currentFolderPath]}
                onNavigate={handleBreadcrumbNavigate}
              />
            )}

            {images.length === 0 ? (
              <div className="flex flex-col justify-center items-center h-64 px-4">
                <div className="text-center max-w-md">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No images match current filters
                  </h3>
                  {currentFolderPath && (
                    <p className="text-sm text-gray-600 mb-4">
                      Looking in folder: <span className="font-medium">"{currentFolderPath}"</span>
                    </p>
                  )}
                  {(filters.workflow || filters.jobName || filters.dateFrom || filters.dateTo || filters.status !== 'active') && (
                    <>
                      <div className="mb-4 text-sm text-gray-700">
                        <span className="font-medium">Active filters:</span>
                        <ul className="mt-2 space-y-1">
                          {filters.status !== 'active' && <li>• Status: {filters.status}</li>}
                          {filters.workflow && <li>• Workflow: {filters.workflow}</li>}
                          {filters.jobName && <li>• Job: {filters.jobName}</li>}
                          {(filters.dateFrom || filters.dateTo) && (
                            <li>• Date: {filters.dateFrom || '...'} to {filters.dateTo || '...'}</li>
                          )}
                        </ul>
                      </div>
                      <button
                        onClick={() => setFilters({
                          folder: currentFolderPath ? currentFolderPath : null,
                          jobId: jobId || null,
                          jobName: null,
                          workflow: null,
                          dateFrom: null,
                          dateTo: null,
                          search: '',
                          status: 'active'
                        })}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Clear Filters
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>

            {/* Root View: Show Folder Cards + Ungrouped Images */}
            {currentFolderPath === null ? (
              <>
                {/* Folder Cards Grid - Hide when filtering by specific job */}
                {folders.length > 0 && !filters.jobId && !filters.jobName && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Folders</h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                      {folders.map((folder) => (
                        <FolderCard
                          key={folder.id}
                          name={folder.name}
                          color={folder.color}
                          imageCount={folder.image_count || 0}
                          videoCount={folder.video_count || 0}
                          previewImages={folder.preview_images || []}
                          onClick={() => navigateToFolder(folder.name)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Ungrouped Images Section */}
                {images.filter(img => !img.folder_path).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Ungrouped Images ({images.filter(img => !img.folder_path).length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {images.filter(img => !img.folder_path).map((image) => {
                        const globalIndex = images.findIndex(img => img.id === image.id);
                        return (
                          <div
                            key={image.id}
                            className={`aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 group relative hover:scale-105 ${
                              selectedImageIds.has(image.id) ? 'ring-4 ring-blue-500 ring-opacity-75' : ''
                            }`}
                            onClick={(e) => handleImageSelect(image.id, e)}
                          >
                            {/* Selection Checkbox */}
                            <div className={`absolute top-2 left-2 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                              selectedImageIds.has(image.id)
                                ? 'bg-blue-500 border-blue-600'
                                : isSelectionMode
                                  ? 'bg-white border-gray-400 hover:border-blue-500'
                                  : 'bg-black bg-opacity-50 border-white'
                            }`}>
                              {selectedImageIds.has(image.id) ? (
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : isSelectionMode ? (
                                <div className="w-3 h-3 border border-gray-400 rounded-sm bg-white"></div>
                              ) : (
                                <span className="text-white text-xs font-medium">{globalIndex + 1}</span>
                              )}
                            </div>

                            {isVideo(image.file_path) ? (
                              <div className="relative w-full h-full">
                                <video
                                  src={image.file_path}
                                  className="w-full h-full object-contain"
                                  preload="metadata"
                                />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <div className="bg-black bg-opacity-60 rounded-full p-3">
                                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={image.thumbnail_path || image.file_path}
                                alt={`${image.job_name}`}
                                className="w-full h-full object-contain"
                                loading="lazy"
                              />
                            )}

                            {/* Hover overlay with info */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 flex items-end">
                              <div className="p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity w-full">
                                <div className="text-sm font-medium truncate">{image.job_name}</div>
                                <div className="text-xs text-gray-300 truncate">{image.workflow_name}</div>
                                <div className="text-xs text-gray-400 mt-1">
                                  {image.user_tags && image.user_tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {image.user_tags.slice(0, 2).map((tag, tagIndex) => (
                                        <span key={tagIndex} className="bg-gray-700 px-1 py-0.5 rounded text-xs">
                                          {tag}
                                        </span>
                                      ))}
                                      {image.user_tags.length > 2 && (
                                        <span className="text-gray-400">+{image.user_tags.length - 2}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Folder View: Show Images in Current Folder */
              <>
                {images.length === 0 ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                      <div className="text-gray-500 mb-2">This folder is empty</div>
                      <div className="text-sm text-gray-400">
                        Move images to this folder using bulk operations or keyboard shortcuts
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {images.map((image) => {
                      const globalIndex = images.findIndex(img => img.id === image.id);
                      return (
                        <div
                          key={image.id}
                          className={`aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 group relative hover:scale-105 ${
                            selectedImageIds.has(image.id) ? 'ring-4 ring-blue-500 ring-opacity-75' : ''
                          }`}
                          onClick={(e) => handleImageSelect(image.id, e)}
                        >
                          {/* Selection Checkbox */}
                          <div className={`absolute top-2 left-2 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                            selectedImageIds.has(image.id)
                              ? 'bg-blue-500 border-blue-600'
                              : isSelectionMode
                                ? 'bg-white border-gray-400 hover:border-blue-500'
                                : 'bg-black bg-opacity-50 border-white'
                          }`}>
                            {selectedImageIds.has(image.id) ? (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : isSelectionMode ? (
                              <div className="w-3 h-3 border border-gray-400 rounded-sm bg-white"></div>
                            ) : (
                              <span className="text-white text-xs font-medium">{globalIndex + 1}</span>
                            )}
                          </div>

                          {isVideo(image.file_path) ? (
                            <div className="relative w-full h-full">
                              <video
                                src={image.file_path}
                                className="w-full h-full object-contain"
                                preload="metadata"
                              />
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-black bg-opacity-60 rounded-full p-3">
                                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={image.thumbnail_path || image.file_path}
                              alt={`${image.job_name}`}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          )}

                          {/* Hover overlay with info */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 flex items-end">
                            <div className="p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity w-full">
                              <div className="text-sm font-medium truncate">{image.job_name}</div>
                              <div className="text-xs text-gray-300 truncate">{image.workflow_name}</div>
                              <div className="text-xs text-gray-400 mt-1">
                                {image.user_tags && image.user_tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {image.user_tags.slice(0, 2).map((tag, tagIndex) => (
                                      <span key={tagIndex} className="bg-gray-700 px-1 py-0.5 rounded text-xs">
                                        {tag}
                                      </span>
                                    ))}
                                    {image.user_tags.length > 2 && (
                                      <span className="text-gray-400">+{image.user_tags.length - 2}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Loading indicator for infinite scroll */}
            {isLoadingMore && (
              <div className="flex justify-center items-center py-8">
                <div className="text-gray-600">Loading more images...</div>
              </div>
            )}

            {/* End of results indicator */}
            {!hasMore && images.length > 0 && (
              <div className="flex justify-center items-center py-8">
                <div className="text-gray-500 text-sm">No more images to load</div>
              </div>
            )}
              </>
            )}
          </>
        )}

        {/* Bulk Operations Toolbar */}
        <BulkOperationsToolbar
          selectedCount={selectedImageIds.size}
          folders={folders}
          onBulkArchive={handleBulkArchive}
          onBulkMoveToFolder={handleBulkMoveToFolder}
          onClearSelection={clearSelection}
          onSelectAll={selectAllImages}
          totalImages={images.length}
        />

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <ImageLightbox
            images={images}
            currentIndex={lightboxIndex}
            onClose={closeLightbox}
            onImageChange={handleLightboxImageChange}
            onArchive={handleArchive}
            onImageUpdate={handleImageUpdate}
          />
        )}

        {/* Folder Manager Modal */}
        <FolderManager
          isOpen={showFolderManager}
          onClose={() => setShowFolderManager(false)}
          onFoldersChange={handleFoldersChange}
        />
      </div>
    </div>
  );
};

export default ImageGallery;
